import { RelativeString } from '@components/DateRangePicker/util/relative';
import { DatePickerVersion2 } from '@components/DatepickerV2';
import { useHasPermissionFromContext } from '@components/PermissionsContext';
import { useViewOnly } from '@components/ViewOnly';
import { DateValue } from '@react-types/datepicker';
import { FullStoryElementType, FullStoryTypes } from '@utils/fullstory';
import {
  IANATimezones,
  currentTZ,
  dateFnsFormat,
  shiftDateByTimezone,
} from '@utils/time';
import { isValid, subDays } from 'date-fns';
import { isString } from 'lodash-es';
import { ReactElement, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { ReadOnlyField } from '../Field/ReadOnlyField';

interface DatePickerV2BaseProps {
  id?: string;
  readOnly?: boolean;
  value?: Date | null | string;
  name?: string;
  'aria-invalid'?: boolean;
  limitDateRange?: boolean;
  dateFormatting?: boolean; // keep for v2
  showOnlyYear?: boolean; // keep for v2
  timezone?: IANATimezones; // keep for v2
  selected?: Date | null; // keep v2
  ['data-testid']?: string;
  relative?: RelativeString | null;
  onBlur?: (event: anyOk) => void;
  onFocus?: (event: anyOk) => void;
  onOpenChange?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
  nonClearable?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  required?: boolean;
  /** filterDate is mapped to isDateUnavailabled prop in DatePicker Version 2 */
  filterDate?: (date: Date | DateValue) => boolean;
  hideRelativeOptions?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  showYears?: boolean;
}

export interface PropsWithTruthyOnChange extends DatePickerV2BaseProps {
  onChange?: (d: Date, relative: RelativeString | null) => void;
  /** `nonClearable` : the datepicker must have a Date. If the user blurs the component without a valid date, it will be set to the beginning of today's date in the proper timezone. This prop should not be used by default. */
  nonClearable: boolean;
}

interface PropsWithNullishOnChange extends DatePickerV2BaseProps {
  onChange?: (d: Date | null, relative: RelativeString | null) => void;
  /** `nonClearable` : the datepicker must have a Date. If the user blurs the component without a valid date, it will be set to the beginning of today's date in the proper timezone. This prop should not be used by default. */
  nonClearable?: undefined;
}

// ts-unused-exports:disable-next-line
export type Props = {
  fsName?: string;
  fsParent?: string;
  fsType?: FullStoryTypes;
  fsElement?: FullStoryElementType;
} & (PropsWithTruthyOnChange | PropsWithNullishOnChange);

const coerceToTimezone = (
  date: Date | string,
  timezoneFrom: string,
  timezoneTo: string,
): Date => {
  if (!timezoneFrom || !timezoneTo || timezoneFrom === timezoneTo) {
    return new Date(date);
  }
  return shiftDateByTimezone(new Date(date), timezoneFrom, timezoneTo);
};

export function DatePicker(props: Props): ReactElement {
  const {
    onChange,
    limitDateRange,
    minDate,
    dateFormatting,
    showOnlyYear,
    timezone,
    showYears,
    defaultOpen,
    fsName,
    fsParent,
    fsType,
    fsElement,
  } = props;
  const { isViewOnly } = useViewOnly();
  const [userHasPermission, permissionScope] = useHasPermissionFromContext();
  const readOnly = !userHasPermission || props.readOnly;

  const resolvedTZ = timezone || currentTZ;

  const valOrSelection = isString(props.value)
    ? props.value
      ? new Date(props.value)
      : null
    : props.value || props.selected;

  const coercedSelection = coerceToTimezone(
    valOrSelection || new Date(),
    resolvedTZ,
    currentTZ,
  );
  const dateFormat = showOnlyYear
    ? 'yyyy'
    : dateFormatting
      ? 'MM/dd/yy'
      : 'MM/dd';

  const prevTZ = usePrevious(timezone);

  useEffect(() => {
    if (prevTZ && prevTZ !== timezone && valOrSelection) {
      const newVal = coerceToTimezone(
        valOrSelection || new Date(),
        prevTZ,
        resolvedTZ,
      );
      onChange?.(newVal, props.relative ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevTZ, timezone]);

  if (readOnly && coercedSelection) {
    return (
      <ReadOnlyField data-scope={permissionScope}>
        {valOrSelection ? dateFnsFormat(coercedSelection, dateFormat) : ''}
      </ReadOnlyField>
    );
  }

  if (isViewOnly && coercedSelection) {
    return (
      <div data-testid={props.id || props.name}>
        {valOrSelection ? dateFnsFormat(coercedSelection, dateFormat) : ''}
      </div>
    );
  }

  return (
    <DatePickerVersion2
      fsName={fsName ?? props?.name}
      fsParent={fsParent}
      fsType={fsType}
      fsElement={fsElement}
      data-testid={props?.['data-testid'] ?? null}
      id={props?.id ?? null}
      name={props?.name ?? undefined}
      hideRelativeOptions={props?.hideRelativeOptions ?? true}
      aria-invalid={props['aria-invalid']}
      value={valOrSelection}
      onChange={({ value, relative: relativeOptionSelected }): void => {
        if (props?.onChange) {
          props.onChange?.(
            (value as Date) ?? null,
            relativeOptionSelected ?? null,
          );
        }
      }}
      timezone={resolvedTZ}
      relative={props.relative || null}
      onBlur={(e): void => {
        if (props?.onBlur) {
          props?.onBlur(e);
        }
      }}
      onFocus={(e): void => {
        if (props?.onFocus) {
          props?.onFocus(e);
        }
      }}
      onOpenChange={(isOpen): void => {
        if (props?.onOpenChange) {
          props?.onOpenChange(isOpen);
        }
      }}
      showYears={showYears || dateFormatting ? true : false}
      readOnly={readOnly}
      defaultOpen={defaultOpen ?? false}
      minValue={
        isValid(props?.minDate) || limitDateRange
          ? props.minDate
          : limitDateRange
            ? minDate || subDays(new Date(), 60)
            : subDays(new Date(), 32850)
      }
      maxValue={isValid(props?.maxDate) ? props?.maxDate : undefined}
      isDisabled={props?.disabled ?? false}
      nonClearable={props?.nonClearable ?? false}
      autoFocus={props?.autoFocus ?? false}
      isRequired={props?.required ?? false}
      isDateUnavailable={(date: DateValue): boolean => {
        if (props?.filterDate) {
          return props.filterDate(date);
        }
        return false;
      }}
    />
  );
}
